import React, { useState, useEffect, useId } from 'react';
import axiosInstance from '../utils/axiosInstance';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import Dialog from './Dialog';

interface BankInfoProps {
    isOpen: boolean;

    name: string;
    bank: string;
    account: string;
    phoneNo: string;

    closeModal?: (refresh: boolean) => void;


}

/** One labelled field. Underline only — a full box on four stacked inputs
 *  reads as a form to be endured rather than a detail to be confirmed. */
const Field: React.FC<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    inputMode?: 'text' | 'tel' | 'numeric';
}> = ({ label, value, onChange, placeholder, inputMode = 'text' }) => {
    const id = useId();
    return (
        <div className='flex flex-col gap-1.5'>
            <label
                htmlFor={id}
                className='text-[11px] uppercase tracking-[0.16em] text-brand-600'
            >
                {label}
            </label>
            <input
                id={id}
                type='text'
                inputMode={inputMode}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className={
                    'w-full border-0 border-b border-solid border-brand-300 bg-transparent ' +
                    'pb-2 text-base text-brand-900 outline-none transition-colors duration-150 ' +
                    'placeholder:text-brand-300 focus:border-accent-500'
                }
            />
        </div>
    );
};

const BankInfo: React.FC<BankInfoProps> = ({
    isOpen,
    closeModal,
    name,
    bank,
    account,
    phoneNo,
}) => {

    const [userName, setUserName] = useState(name);
    const [userBank, setUserBank] = useState(bank);
    const [userAccount, setUserAccount] = useState(account);
    const [userPhoneNo, setUserPhoneNo] = useState(phoneNo);

    const { t } = useTranslation();

    useEffect(() => {
        setUserName(name);
    }, [name])
    useEffect(() => {
        setUserBank(bank);
    }, [bank])
    useEffect(() => {
        setUserAccount(account);
    }, [account])
    useEffect(() => {
        setUserPhoneNo(phoneNo);
    }, [phoneNo])

    const saveBank = async () => {
        try {
            const res = await axiosInstance.post("/protected/saveBankInfo", {
                name: userName,
                bank: userBank,
                account: userAccount,
                phoneNo: userPhoneNo
            })
            if (res.status === 201) {
                // completed
                Swal.fire({
                    title: 'Success!',
                    text: 'Saved successfully',
                    icon: 'success',
                }).then(e => {
                    if (closeModal)
                        closeModal(true);
                })
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: 'Failed to save',
                    icon: 'error',
                })
            }
        } catch {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to save',
                icon: 'error',
            })
        }
    }

    const close = () => {
        if (closeModal)
            closeModal(false)
    }

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            title='Bank Info'
            footer={
                <>
                    <button
                        type='button'
                        onClick={close}
                        className={
                            'cursor-pointer rounded-md border border-solid border-brand-300 bg-transparent ' +
                            'px-5 py-2.5 text-xs uppercase tracking-[0.16em] text-brand-600 ' +
                            'transition-colors duration-150 hover:border-brand-600 hover:text-brand-900 ' +
                            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                            'focus-visible:outline-accent-500'
                        }
                    >
                        {t("close")}
                    </button>
                    <button
                        type='button'
                        onClick={saveBank}
                        className={
                            'cursor-pointer rounded-md border-0 bg-brand-800 px-6 py-2.5 ' +
                            'text-xs font-semibold uppercase tracking-[0.16em] text-white ' +
                            'transition-colors duration-150 hover:bg-accent-600 active:scale-[.99] ' +
                            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                            'focus-visible:outline-accent-500'
                        }
                    >
                        {t("save")}
                    </button>
                </>
            }
        >
            <div className='flex flex-col gap-6'>
                <Field
                    label={t("fullname")}
                    value={userName}
                    onChange={setUserName}
                    placeholder={t("type_here")}
                />
                <Field
                    label={t("bankaccount")}
                    value={userBank}
                    onChange={setUserBank}
                    placeholder={t("type_here")}
                />
                <Field
                    label={t("accountno")}
                    value={userAccount}
                    onChange={setUserAccount}
                    placeholder={t("type_here")}
                    inputMode='numeric'
                />
                <Field
                    label={t("phoneNo")}
                    value={userPhoneNo}
                    onChange={setUserPhoneNo}
                    placeholder={t("type_here")}
                    inputMode='tel'
                />
            </div>
        </Dialog>
    );
};

export default BankInfo;
